<template>
  <div class="cbs-html-sandbox">
    <div
      v-if="fatal"
      class="cbs-html-sandbox__error text-muted d-flex align-items-center justify-content-center"
      :style="{ minHeight: minHeight + 'px' }"
    >
      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="20"
        />
        <div class="mt-50">
          {{ fatal }}
        </div>
      </div>
    </div>
    <iframe
      v-else
      ref="frame"
      src="/sandbox.html"
      :style="{ height: frameHeight + 'px' }"
      :title="page && page.sid ? page.sid : 'cubus-html-page'"
      class="cbs-html-sandbox__frame"
      sandbox="allow-scripts allow-forms allow-modals"
      referrerpolicy="no-referrer"
    />
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// BUG-439: проверка «есть ли такой маршрут» одна на песочницу и на ленту чата — иначе вторая
// копия правила разойдётся с первой ровно тогда, когда правило окажется тоньше, чем кажется.
import { routeExists } from '@/cubus/services/chatMarkdown'
import { buildSandboxHtml, newFrameId } from '@/cubus/html-sandbox/cubusPageSdk'
import {
  bridgeError,
  queryObject,
  saveObjectRecord,
  upsertObjectRecord,
  deleteObjectRecord,
  runProcedure,
} from '@/cubus/html-sandbox/htmlPageApi'

// PROP-048: набор операций моста меняется от сборки к сборке (upsert появился только в 157),
// а способа узнать его до вызова не было — страница, написанная под новый метод, на старом
// фронте падала в рантайме (`cubus.upsert is not a function`), а пользователь видел невнятное
// «Something went wrong CODE 16». Список объявляет ХОСТ, а не SDK: диспетчеризует именно он,
// и обёртка в window.cubus может существовать там, где операции у хоста нет.
// v2 = контекст сообщает protocolversion и operations.
const PROTOCOL_VERSION = 2
const OPERATIONS = [
  'getContext', 'query', 'save', 'upsert', 'delete', 'runProc',
  'setValue', 'navigate', 'openExternal', 'toast', 'resize',
]
const READY_TIMEOUT_MS = 10000
const MAX_PAYLOAD_CHARS = 1024 * 1024
const MAX_CONCURRENT = 20
const MAX_REQ_PER_SEC = 30

// Цвета Vuexy для передачи в страницу как --cubus-* переменные
const THEME_COLORS = {
  light: {
    primary: '#7367f0',
    secondary: '#82868b',
    success: '#28c76f',
    danger: '#ea5455',
    warning: '#ff9f43',
    info: '#00cfe8',
    body: '#f8f8f8',
    surface: '#ffffff',
    text: '#6e6b7b',
    heading: '#5e5873',
  },
  dark: {
    primary: '#7367f0',
    secondary: '#82868b',
    success: '#28c76f',
    danger: '#ea5455',
    warning: '#ff9f43',
    info: '#00cfe8',
    body: '#161d31',
    surface: '#283046',
    text: '#b4b7bd',
    heading: '#d0d2d6',
  },
}

export default {
  name: 'CbsHtmlSandbox',
  props: {
    page: {
      type: Object,
      required: true,
    },
    context: {
      type: Object,
      default: null,
    },
    minHeight: {
      type: Number,
      default: 120,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      frameId: newFrameId(),
      frameHeight: this.minHeight,
      fatal: null,
      isReady: false,
      readyTimer: null,
      activeReqs: 0,
      reqTimes: [],
    }
  },
  computed: {
    preparedHtml() {
      return buildSandboxHtml(this.page.html, this.frameId)
    },
    skin() {
      return this.$store.state.appConfig.layout.skin === 'dark' ? 'dark' : 'light'
    },
  },
  watch: {
    context: {
      deep: true,
      handler() {
        if (this.isReady) this.sendEvent('contextChanged', this.buildContext())
      },
    },
    skin() {
      if (this.isReady) this.sendEvent('themeChanged', { theme: this.buildTheme() })
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage)
    this.readyTimer = setTimeout(() => {
      if (!this.isReady) {
        this.fatal = 'Страница не отвечает (нет рукопожатия SDK за 10 с).'
        this.$emit('error', bridgeError('E_TIMEOUT', 'sdk ready timeout'))
      }
    }, READY_TIMEOUT_MS)
  },
  beforeDestroy() {
    if (this.isReady) this.sendEvent('destroy', null)
    if (this.readyTimer) clearTimeout(this.readyTimer)
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    post(msg) {
      const { frame } = this.$refs
      if (!frame || !frame.contentWindow) return
      const m = msg
      m.cubus = true
      m.v = PROTOCOL_VERSION
      m.frame = this.frameId
      frame.contentWindow.postMessage(m, '*')
    },
    sendEvent(type, payload) {
      this.post({ kind: 'event', type, payload })
    },
    // PROP-050: событие `refresh` описано в SDK (cubus.onRefresh), но послать его было некому —
    // родитель вызывает этот метод, страница перезапрашивает данные внутри уже загруженного
    // iframe. Смена самого html — не сюда: srcdoc инжектится один раз при монтировании,
    // её обрабатывает владелец виджета пересозданием фрейма.
    refresh() {
      if (this.isReady) this.sendEvent('refresh', null)
    },
    respond(id, result) {
      this.post({ kind: 'res', id, result })
    },
    respondError(id, err) {
      this.post({
        kind: 'res',
        id,
        error: {
          code: err.code || 'E_INTERNAL',
          message: err.message || String(err),
          details: err.details !== undefined ? err.details : null,
        },
      })
    },

    onMessage(event) {
      const { frame } = this.$refs
      if (!frame || event.source !== frame.contentWindow) return
      const msg = event.data
      // CUB-1418: песочница — `/sandbox.html` со своей политикой CSP (srcdoc наследовал бы строгую политику фронта и
      // скрипты страницы не выполнялись бы): загрузчик сообщает `ready` — отдаём ему готовый документ один раз
      // eslint-disable-next-line no-underscore-dangle
      if (msg && msg.__cubusSandbox === 'ready') {
        frame.contentWindow.postMessage({ __cubusSandbox: 'render', html: this.preparedHtml }, '*')
        return
      }
      if (!msg || msg.cubus !== true || msg.frame !== this.frameId) return
      if (msg.kind === 'event') {
        this.onFrameEvent(msg)
      } else if (msg.kind === 'req') {
        this.onFrameRequest(msg)
      }
    },

    onFrameEvent(msg) {
      if (msg.type === 'ready') {
        if (this.readyTimer) clearTimeout(this.readyTimer)
        if (msg.payload && Number(msg.payload.v) > PROTOCOL_VERSION) {
          this.fatal = 'Несовместимая версия протокола страницы.'
          this.$emit('error', bridgeError('E_PROTOCOL', 'incompatible protocol version'))
          return
        }
        this.isReady = true
        this.sendEvent('init', this.buildContext())
        this.$emit('ready')
      } else if (msg.type === 'action') {
        const p = msg.payload || {}
        this.$emit('action', p.name, p.data)
      }
    },

    onFrameRequest(msg) {
      if (!msg.id || typeof msg.type !== 'string') return
      try {
        this.checkLimits(msg)
      } catch (err) {
        this.respondError(msg.id, err)
        return
      }
      this.activeReqs += 1
      Promise.resolve()
        .then(() => this.dispatch(msg.type, msg.payload || {}))
        .then(result => this.respond(msg.id, result))
        .catch(err => {
          console.error(`[CbsHtmlSandbox] page="${this.page.sid}" req="${msg.type}" failed:`, err)
          this.respondError(msg.id, err)
        })
        .finally(() => { this.activeReqs -= 1 })
    },

    checkLimits(msg) {
      if (JSON.stringify(msg).length > MAX_PAYLOAD_CHARS) {
        throw bridgeError('E_PAYLOAD_TOO_LARGE', 'message exceeds 1 MB')
      }
      if (this.activeReqs >= MAX_CONCURRENT) {
        throw bridgeError('E_RATE_LIMIT', `more than ${MAX_CONCURRENT} concurrent requests`)
      }
      const now = Date.now()
      this.reqTimes = this.reqTimes.filter(t => now - t < 1000)
      if (this.reqTimes.length >= MAX_REQ_PER_SEC) {
        throw bridgeError('E_RATE_LIMIT', `more than ${MAX_REQ_PER_SEC} requests per second`)
      }
      this.reqTimes.push(now)
    },

    dispatch(type, payload) {
      // Заявленный список — единственный источник правды: новая ветка switch без строки в
      // OPERATIONS будет отвергнута здесь, поэтому обещание странице не разойдётся с делом.
      if (OPERATIONS.indexOf(type) === -1) {
        throw bridgeError('E_UNSUPPORTED', `unknown request type "${type}"`, { operations: OPERATIONS })
      }
      switch (type) {
        case 'getContext': return this.buildContext()
        case 'query':
          this.assertObjectAllowed(payload.objectsid)
          return queryObject(payload)
        case 'save':
          this.assertObjectAllowed(payload.objectsid)
          return saveObjectRecord(payload.objectsid, payload.record)
        case 'upsert':
          this.assertObjectAllowed(payload.objectsid)
          return upsertObjectRecord(payload.objectsid, payload.record, payload.uniqueKeys)
        case 'delete':
          this.assertObjectAllowed(payload.objectsid)
          return deleteObjectRecord(payload.objectsid, payload.record)
        case 'runProc':
          this.assertProcAllowed(payload.procsid)
          return runProcedure(payload.procsid, payload.params)
        case 'setValue':
          return this.handleSetValue(payload)
        case 'navigate':
          return this.handleNavigate(payload)
        case 'openExternal':
          return this.handleOpenExternal(payload)
        case 'toast':
          return this.handleToast(payload)
        case 'resize':
          return this.handleResize(payload)
        default:
          throw bridgeError('E_INTERNAL', `operation "${type}" is declared but not implemented`, { operations: OPERATIONS })
      }
    },

    assertObjectAllowed(objectsid) {
      const list = this.page.objectwhitelist || []
      if (!objectsid || list.indexOf(objectsid) === -1) {
        throw bridgeError('E_DENIED', `object "${objectsid}" is not in page whitelist`)
      }
    },
    assertProcAllowed(procsid) {
      const list = this.page.procwhitelist || []
      if (!procsid || list.indexOf(procsid) === -1) {
        throw bridgeError('E_DENIED', `procedure "${procsid}" is not in page whitelist`)
      }
    },

    handleSetValue(payload) {
      if (!this.context || this.context.mode !== 'form') {
        throw bridgeError('E_UNSUPPORTED', 'setValue is available only in form mode')
      }
      this.$emit('setvalue', payload.attribute, payload.value)
      return { ok: true }
    },

    handleNavigate(payload) {
      // BUG-427: переход по ссылке САМОЙ страницы. Путь проверяем ЗДЕСЬ, а не только в SDK:
      // содержимое страницы пишет модель, а до хоста запрос доходит через postMessage — то есть
      // из фрейма может прийти что угодно. Пускаем только путь от корня: `//чужой.сайт` — это
      // внешний адрес в маскировке, и по нему браузер увёл бы человека с платформы.
      const path = typeof payload.path === 'string' ? payload.path.trim() : ''
      if (path) {
        if (path.indexOf('/') !== 0 || path.indexOf('//') === 0) {
          throw bridgeError('E_DENIED', `only internal paths are allowed, got "${path}"`)
        }
        /**
         * BUG-439: несуществующий путь — это НЕ переход, а 404.
         *
         * Содержимое страницы пишет модель, и адрес она может сочинить: живой прогон дал пять
         * ссылок вида `/cubus/object` (без параметра) — маршрут объекта требует `:objectid`, и
         * человек попадал на 404. Уводить его туда молча нельзя: со стороны это выглядит как
         * поломка платформы, а не как выдуманная ссылка.
         *
         * Проверяем самим маршрутизатором: у ненайденного пути совпадает только catch-all «*».
         */
        if (!routeExists(this.$router, path)) {
          /**
           * Своё окошко, а не общий `toastError`: у того заголовок «Something went wrong CODE 16»,
           * и человек читает выдуманную агентом ссылку как поломку платформы — ровно то
           * впечатление, ради устранения которого этот заслон и ставился. Видно в приёмке.
           */
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Ссылка никуда не ведёт',
              icon: 'LinkIcon',
              variant: 'warning',
              text: `Такого раздела нет: ${path}. Похоже, агент выдумал адрес — попросите его`
                + ' назвать объект по имени.',
            },
          })
          throw bridgeError('E_DENIED', `path "${path}" does not match any route`)
        }
        // Повтор того же маршрута vue-router считает ошибкой навигации — для человека это
        // просто «нажал на ссылку на самого себя», а не отказ.
        if (this.$route.fullPath !== path) this.$router.push(path).catch(() => {})
        return { ok: true }
      }
      const objectid = payload.objectid || payload.objectsid
      if (!objectid) throw bridgeError('E_DENIED', 'only internal object navigation is allowed')
      if (payload.recordid !== undefined && payload.recordid !== null) {
        this.$router.push({ path: `/cubus/recordpage/${objectid}/${payload.recordid}` })
      } else {
        this.$router.push({ name: 'cubus-object', params: { objectid } })
      }
      return { ok: true }
    },

    handleOpenExternal(payload) {
      // PROP-428: внешняя ссылка страницы — в новой вкладке, а не внутри рамки. Адрес приходит из
      // фрейма, содержимое которого пишет модель, поэтому схему проверяем здесь: только http(s),
      // `javascript:` и `data:` вкладку не получат. `opener` обрезается — чужой сайт не должен
      // дотянуться до вкладки Cubus (`noopener` в окне не ставим: с ним `window.open` всегда
      // возвращает null и заблокированное окно было бы не отличить от открытого).
      let url
      try {
        url = new URL(String(payload.url || ''))
      } catch (e) {
        throw bridgeError('E_DENIED', `not a valid address: "${payload.url}"`)
      }
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw bridgeError('E_DENIED', `only http(s) links open in a new tab, got "${url.protocol}"`)
      }
      const tab = window.open(url.href, '_blank')
      if (tab) {
        tab.opener = null
        return { ok: true }
      }
      // Окно заблокировано браузером — адрес человеку всё равно показываем: ссылку можно открыть
      // самому, а молча проглоченный клик выглядит как поломка.
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        timeout: 15000,
        props: {
          title: 'Браузер не дал открыть вкладку',
          icon: 'ExternalLinkIcon',
          variant: 'warning',
          text: `Внешняя ссылка: ${url.href}. Разрешите всплывающие окна для Cubus или откройте адрес вручную.`,
        },
      })
      throw bridgeError('E_DENIED', 'the browser blocked the new tab')
    },

    handleToast(payload) {
      const message = String(payload.message || '')
      if (payload.kind === 'error') useCubus.toastError(this, message)
      else useCubus.toastMessage(this, message)
      return { ok: true }
    },

    handleResize(payload) {
      let h = Number(payload.height) || this.minHeight
      if (h < this.minHeight) h = this.minHeight
      if (this.maxHeight && h > this.maxHeight) h = this.maxHeight
      this.frameHeight = h
      return { ok: true }
    },

    buildTheme() {
      return {
        skin: this.skin,
        colors: THEME_COLORS[this.skin],
      }
    },

    buildContext() {
      const session = this.$store.getters['cubus-store/SESSION']
      const user = session && session.user ? {
        id: session.user.id,
        sid: session.user.sid || null,
        name: session.user.name || null,
        role: session.user.role ? session.user.role.sid || session.user.role.name : null,
      } : null
      const base = {
        mode: 'widget',
        // BUG-427: своё происхождение песочница знает только от нас — у неё оно обнулено
        // (`sandbox` без `allow-same-origin`). Нужно, чтобы отличить ссылку на СВОЮ установку,
        // написанную полным адресом, от ссылки на чужой сайт.
        origin: window.location.origin,
        user,
        locale: (this.$i18n && this.$i18n.locale) || 'ru',
        theme: this.buildTheme(),
      }
      // Поля контракта ставятся ПОСЛЕ контекста родителя: заявлять возможности моста может
      // только сам мост, иначе форма или дэшборд могли бы их случайно переопределить.
      return Object.assign(base, this.context || {}, {
        protocol: PROTOCOL_VERSION,
        protocolversion: PROTOCOL_VERSION,
        operations: OPERATIONS,
      })
    },
  },
}
</script>

<style scoped>
.cbs-html-sandbox__frame {
  width: 100%;
  border: 0;
  display: block;
}
.cbs-html-sandbox__error {
  width: 100%;
}
</style>
